/**
 * AI WhatsApp Panel Component
 * 
 * Pannello laterale che mostra analisi AI della conversazione WhatsApp,
 * bozza risposta modificabile, azioni suggerite, e allegati fatture/estratti.
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  TextField,
  Skeleton,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Psychology as AiIcon,
  ExpandMore as ExpandIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Lightbulb as TipIcon,
  FormatListBulleted as StepsIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  Receipt as InvoiceIcon,
  AccountBalance as StatementIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Mapping categorie WhatsApp
const WA_CATEGORY_CONFIG = {
  richiesta_info: { label: 'Richiesta Info', color: '#1976d2', icon: '❓' },
  problema_tecnico: { label: 'Problema Tecnico', color: '#d32f2f', icon: '🔧' },
  pagamento: { label: 'Pagamento', color: '#388e3c', icon: '💳' },
  lettura_contatore: { label: 'Lettura Contatore', color: '#7b1fa2', icon: '📊' },
  appuntamento: { label: 'Appuntamento', color: '#f57c00', icon: '📅' },
  reclamo: { label: 'Reclamo', color: '#c62828', icon: '⚠️' },
  sollecito: { label: 'Sollecito', color: '#e65100', icon: '📢' },
  comunicazione_generica: { label: 'Comunicazione', color: '#546e7a', icon: '💬' },
  altro: { label: 'Altro', color: '#757575', icon: '📋' },
};

export default function AiWhatsappPanel({
  open,
  onClose,
  phoneNumber,
  customerId,
  analysis,       // { category, confidence, rationale, urgency, reply_draft, suggested_actions, next_steps, crm_actions, needs_invoice_attachment, needs_statement_attachment }
  actionId,
  invoicesData,   // [{id, nuf, f_emission, f_totale, pagata, parziale, saldo_residuo}]
  loading = false,
  error = null,
  onAnalyze,
  onApprove,      // (actionId, attachments) => Promise
  onReject,
  onRegenerate,   // (actionId, customPrompt, tone) => Promise
  onEditDraft,    // (actionId, text) => Promise
  onInsertDraft,  // (text) => void — inserisci nel campo messaggio
}) {
  const [editedDraft, setEditedDraft] = useState('');
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [copiedDraft, setCopiedDraft] = useState(false);
  const [expanded, setExpanded] = useState(['draft', 'actions', 'steps', 'attachments']);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showRegenerateInput, setShowRegenerateInput] = useState(false);
  const [tone, setTone] = useState('');
  const [sending, setSending] = useState(false);

  // Reset state when phone changes
  useEffect(() => {
    setEditedDraft('');
    setIsEditingDraft(false);
    setCopiedDraft(false);
    setSelectedAttachments([]);
    setCustomPrompt('');
    setShowRegenerateInput(false);
  }, [phoneNumber]);

  // Update draft when analysis changes
  useEffect(() => {
    if (analysis?.reply_draft) {
      setEditedDraft(analysis.reply_draft);
    }
  }, [analysis?.reply_draft]);

  // Auto-select attachments if AI suggests them
  useEffect(() => {
    if (analysis?.needs_invoice_attachment && invoicesData?.length > 0) {
      const unpaid = invoicesData.filter(i => !i.pagata);
      if (unpaid.length > 0) {
        setSelectedAttachments(prev => {
          const existing = prev.map(a => `${a.type}_${a.id}`);
          const newAtts = unpaid
            .filter(i => !existing.includes(`invoice_${i.id}`))
            .map(i => ({ type: 'invoice', id: i.id, filename: `fattura_${i.nuf || i.id}.pdf` }));
          return [...prev, ...newAtts];
        });
      }
    }
    if (analysis?.needs_statement_attachment && customerId) {
      setSelectedAttachments(prev => {
        if (!prev.find(a => a.type === 'account_statement')) {
          return [...prev, { type: 'account_statement', id: customerId, filename: 'estratto_conto.pdf' }];
        }
        return prev;
      });
    }
  }, [analysis, invoicesData, customerId]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(prev =>
      isExpanded ? [...prev, panel] : prev.filter(p => p !== panel)
    );
  };

  const handleCopyDraft = async () => {
    const text = isEditingDraft ? editedDraft : analysis?.reply_draft;
    if (text) {
      await navigator.clipboard.writeText(text);
      setCopiedDraft(true);
      setTimeout(() => setCopiedDraft(false), 2000);
    }
  };

  const handleInsertDraft = () => {
    const text = isEditingDraft ? editedDraft : analysis?.reply_draft;
    if (onInsertDraft && text) onInsertDraft(text);
  };

  const handleApproveAndSend = async () => {
    if (!actionId || !onApprove) return;
    setSending(true);
    try {
      // If draft was edited, save first
      if (isEditingDraft && editedDraft !== analysis?.reply_draft && onEditDraft) {
        await onEditDraft(actionId, editedDraft);
      }
      await onApprove(actionId, selectedAttachments.length > 0 ? selectedAttachments : undefined);
    } finally {
      setSending(false);
    }
  };

  const handleRegenerate = async () => {
    if (!actionId || !onRegenerate) return;
    await onRegenerate(actionId, customPrompt || undefined, tone || undefined);
    setShowRegenerateInput(false);
    setCustomPrompt('');
  };

  const toggleInvoiceAttachment = (invoice) => {
    setSelectedAttachments(prev => {
      const key = `invoice_${invoice.id}`;
      const exists = prev.find(a => a.type === 'invoice' && a.id === invoice.id);
      if (exists) return prev.filter(a => !(a.type === 'invoice' && a.id === invoice.id));
      return [...prev, { type: 'invoice', id: invoice.id, filename: `fattura_${invoice.nuf || invoice.id}.pdf` }];
    });
  };

  const toggleStatementAttachment = () => {
    if (!customerId) return;
    setSelectedAttachments(prev => {
      const exists = prev.find(a => a.type === 'account_statement');
      if (exists) return prev.filter(a => a.type !== 'account_statement');
      return [...prev, { type: 'account_statement', id: customerId, filename: 'estratto_conto.pdf' }];
    });
  };

  const catConfig = WA_CATEGORY_CONFIG[analysis?.category] || WA_CATEGORY_CONFIG.altro;
  const panelWidth = 500;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!isMobile && !open) return null;

  const panelInner = (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#25D366', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon />
          <Typography variant="h6" fontWeight="bold">AI WhatsApp</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Disclaimer */}
      <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ borderRadius: 0, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
        La risposta viene inviata solo dopo la tua conferma.
      </Alert>

      {/* Content */}
      <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0, p: 2 }}>

        {/* Not analyzed */}
        {!analysis && !loading && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AiIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Conversazione non analizzata
            </Typography>
            <Button variant="contained" startIcon={<AiIcon />} onClick={onAnalyze} sx={{ mt: 2, bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}>
              Analizza con AI
            </Button>
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CircularProgress size={24} />
              <Typography>Analisi conversazione...</Typography>
            </Box>
            <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={60} />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Errore Analisi</AlertTitle>
            {error}
            <Button size="small" onClick={onAnalyze} sx={{ mt: 1 }}>Riprova</Button>
          </Alert>
        )}

        {/* Analysis Results */}
        {analysis && !loading && (
          <>
            {/* Classification */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Classificazione
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={`${catConfig.icon} ${catConfig.label}`}
                  sx={{ bgcolor: catConfig.color, color: '#fff', fontWeight: 'bold' }}
                />
                <Chip
                  size="small"
                  label={`${Math.round((analysis.confidence || 0) * 100)}%`}
                  variant="outlined"
                />
                {analysis.urgency && analysis.urgency !== 'bassa' && (
                  <Chip
                    size="small"
                    label={`Urgenza: ${analysis.urgency}`}
                    color={analysis.urgency === 'alta' ? 'error' : 'warning'}
                  />
                )}
              </Box>
              {analysis.rationale && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {analysis.rationale}
                </Typography>
              )}
            </Paper>

            {/* Reply Draft */}
            {analysis.reply_draft && (
              <Accordion expanded={expanded.includes('draft')} onChange={handleAccordionChange('draft')} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon color="secondary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="medium">Bozza Risposta</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {isEditingDraft ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      value={editedDraft}
                      onChange={(e) => setEditedDraft(e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#dcf8c6', maxHeight: 200, overflow: 'auto', fontSize: '0.875rem', whiteSpace: 'pre-wrap', borderRadius: 2 }}>
                      {analysis.reply_draft}
                    </Paper>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                    <Button size="small" variant={isEditingDraft ? 'outlined' : 'text'} startIcon={<EditIcon />} onClick={() => setIsEditingDraft(!isEditingDraft)}>
                      {isEditingDraft ? 'Fine' : 'Modifica'}
                    </Button>
                    <Button size="small" variant="text" startIcon={copiedDraft ? <CheckIcon /> : <CopyIcon />} onClick={handleCopyDraft} color={copiedDraft ? 'success' : 'inherit'}>
                      {copiedDraft ? 'Copiato!' : 'Copia'}
                    </Button>
                    <Button size="small" variant="outlined" onClick={handleInsertDraft}>
                      Inserisci
                    </Button>
                    <Button size="small" variant="text" startIcon={<RefreshIcon />} onClick={() => setShowRegenerateInput(!showRegenerateInput)}>
                      Rigenera
                    </Button>
                  </Stack>

                  {/* Regenerate with custom prompt */}
                  {showRegenerateInput && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Istruzioni per rigenerare (opzionale)..."
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <Stack direction="row" spacing={1}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Tono</InputLabel>
                          <Select value={tone} onChange={(e) => setTone(e.target.value)} label="Tono">
                            <MenuItem value="">Default</MenuItem>
                            <MenuItem value="formal">Formale</MenuItem>
                            <MenuItem value="friendly">Amichevole</MenuItem>
                            <MenuItem value="concise">Conciso</MenuItem>
                          </Select>
                        </FormControl>
                        <Button size="small" variant="contained" onClick={handleRegenerate}>
                          Rigenera
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Attachments */}
            {customerId && (invoicesData?.length > 0 || true) && (
              <Accordion expanded={expanded.includes('attachments')} onChange={handleAccordionChange('attachments')}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="medium">Allegati PDF</Typography>
                    {selectedAttachments.length > 0 && (
                      <Chip size="small" label={selectedAttachments.length} color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Statement */}
                  <ListItem dense button onClick={toggleStatementAttachment} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={!!selectedAttachments.find(a => a.type === 'account_statement')}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <StatementIcon color="info" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Estratto Conto" secondary="PDF completo del cliente" />
                  </ListItem>

                  {/* Invoices */}
                  {invoicesData && invoicesData.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Fatture recenti:
                      </Typography>
                      {invoicesData.map(inv => {
                        const iconColor = inv.pagata ? 'success' : inv.parziale ? 'warning' : 'error';
                        const statusLabel = inv.pagata
                          ? '✅ Pagata'
                          : inv.parziale
                            ? `⚠️ Parziale${inv.saldo_residuo != null ? `, residuo €${Number(inv.saldo_residuo).toFixed(2)}` : ''}`
                            : '❌ Non pagata';

                        return (
                          <ListItem key={inv.id} dense button onClick={() => toggleInvoiceAttachment(inv)} sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox
                                edge="start"
                                checked={!!selectedAttachments.find(a => a.type === 'invoice' && a.id === inv.id)}
                                size="small"
                              />
                            </ListItemIcon>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <InvoiceIcon color={iconColor} fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`N. ${inv.nuf || inv.id}`}
                              secondary={`€${Number(inv.f_totale || 0).toFixed(2)} — ${inv.f_emission ? new Date(inv.f_emission).toLocaleDateString('it-IT') : '?'} — ${statusLabel}`}
                            />
                          </ListItem>
                        );
                      })}
                    </>
                  )}

                  {(!invoicesData || invoicesData.length === 0) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Nessuna fattura disponibile per questo cliente.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Suggested Actions */}
            {analysis.suggested_actions && analysis.suggested_actions.length > 0 && (
              <Accordion expanded={expanded.includes('actions')} onChange={handleAccordionChange('actions')}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TipIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="medium">Azioni Suggerite</Typography>
                    <Chip size="small" label={analysis.suggested_actions.length} color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {analysis.suggested_actions.map((action, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={action.priority || 'medium'}
                            color={action.priority === 'high' ? 'error' : action.priority === 'medium' ? 'warning' : 'default'}
                          />
                          <Typography variant="body2" fontWeight="medium">{action.action}</Typography>
                        </Box>
                        {action.reason && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {action.reason}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* CRM Actions */}
            {analysis.crm_actions && analysis.crm_actions.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Azioni CRM Suggerite
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {analysis.crm_actions.map((action, idx) => (
                    <Chip key={idx} size="small" label={action.replace(/_/g, ' ')} variant="outlined" color="primary" />
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Next Steps */}
            {analysis.next_steps && analysis.next_steps.length > 0 && (
              <Accordion expanded={expanded.includes('steps')} onChange={handleAccordionChange('steps')}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StepsIcon color="info" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="medium">Prossimi Passi</Typography>
                    <Chip size="small" label={analysis.next_steps.length} color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {analysis.next_steps.map((step, index) => (
                      <Paper key={index} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Chip size="small" label={index + 1} color="info" sx={{ minWidth: 24, height: 24 }} />
                        <Typography variant="body2">{step}</Typography>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* APPROVE / REJECT buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                onClick={handleApproveAndSend}
                disabled={sending || !actionId}
                sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
              >
                {sending ? 'Invio...' : `Invia${selectedAttachments.length > 0 ? ` + ${selectedAttachments.length} PDF` : ''}`}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => onReject && onReject(actionId)}
                disabled={!actionId}
              >
                Rifiuta
              </Button>
            </Box>

            {/* Model info */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
              <Typography variant="caption" color="text.secondary">
                Modello: {analysis.model || '?'} ({analysis.provider || '?'}) • {analysis.analyzed_at && new Date(analysis.analyzed_at).toLocaleString('it-IT')}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </>
  );

  // Mobile: Drawer dal basso
  if (isMobile) {
    return (
      <Drawer anchor="bottom" open={open} onClose={onClose} variant="temporary"
        sx={{
          '& .MuiDrawer-paper': {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}>
        {panelInner}
      </Drawer>
    );
  }

  // Desktop: pannello inline fisso (partecipa al flex layout)
  return (
    <Paper elevation={0} sx={{
      width: panelWidth, minWidth: panelWidth,
      borderLeft: 1, borderColor: 'divider',
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden',
      flexShrink: 0,
    }}>
      {panelInner}
    </Paper>
  );
}
